import React, { useContext, useEffect, useState } from "react";
import tw from "twin.macro";
import { css } from "styled-components/macro"; //eslint-disable-line
import { useNavigate } from "react-router-dom";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import PresentationHeader from "components/columns/TwoColumnWithVideo.js";
import Features from "components/features/ThreeColSimple.js";
import VisitorsHistory from "components/testimonials/ThreeColumnWithProfileImage.js";
import DownloadApp from "components/cta/DownloadApp.js";
import Footer from "components/footers/FourColumnWithInputForm.js";
import RegisterForm from "components/forms/RegisterForm.js";
import celebrationIconImageSrc from "images/celebration-icon.svg";
import adminIcon from "images/admin.svg";
import visitorImageSrc from "images/demo/visitor-page.png";
import historyVisitorImageSrc from "images/demo/historico-visitas.png";
import policeIcon from "images/policeIcon.svg";
import { AppContext } from "components/context/AppContext";
import VisitorConfirmation from "components/features/VisitorConfirmation";
import { useVisitors } from "hooks/useVisitors";
import { NotificationContainer, NotificationManager } from "react-notifications";
import LoadingSpinner from "components/misc/LoadingSpinner.js";
import styled from "styled-components";
import { ROUTE_PATHS } from "utils/routes";

const SpinnerOverlay = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background: rgba(255,255,255,0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const personIcons = [
  require("images/personIco.png"),
  require("images/demo/visit-card.png"),
  require("images/demo/qr-scan.png")
];

const Subheading = tw.span`tracking-wider text-sm font-medium`;
const HighlightedText = tw.span`bg-primary-500 text-gray-100 px-4 transform -skew-x-12 inline-block`;
const HighlightedTextInverse = tw.span`bg-gray-100 text-primary-500 px-4 transform -skew-x-12 inline-block`;
const Description = tw.span`inline-block mt-8`;
const imageCss = tw`rounded-4xl`;

export default () => {
  const { state } = useContext(AppContext);
  const user = state.user;
  const { fetchVisitors, loading } = useVisitors();
  const [visitorsHistory, setVisitorsHistory] = useState([]);
  const navigate = useNavigate();

  const createVisitorCards = (visitors) => {
    return visitors
      .filter(visitor => visitor.invitationStatusId === 2)
      .sort((a, b) => b.visitorId - a.visitorId)
      .map((visitor, idx) => ({
        imageSrc: personIcons[idx % personIcons.length],
        quote: visitor.visitReason + " ** " + visitor.startDate.substring(0, 10) + " ** ",
        customerName: visitor.visitorName
      }));
  };

  const handleVisitorsResponse = (response) => {
    if (response.data.visitors.length > 0 && state.visitors.length > 0) {
      const visitorCards = createVisitorCards(state.visitors);
      setVisitorsHistory((prev) => [...prev, ...visitorCards]);
    }
  };

  const handleVisitorsError = (error) => {
    console.log("Failed to get visitors: " + error);
    NotificationManager.error(error.message, "Error al mostrar Invitaciones", 5000);
  };

  useEffect(() => {
    const getVisitorsData = async (id) => {
      try {
        const response = await fetchVisitors(id);
        handleVisitorsResponse(response);
      } catch (error) {
        handleVisitorsError(error);
      }
    };

    getVisitorsData(user.homeownerId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Redirect to QR confirmation page after visitor is successfully created. */
  const handleVisitorCreated = (guid) => {
    if (guid) {
      navigate(ROUTE_PATHS.VISITORQR.replace(":guid", guid));
    } else {
      navigate(ROUTE_PATHS.VISIT);
    }
  };

  return (
    <>
      {loading && (
        <SpinnerOverlay>
          <LoadingSpinner text="Cargando…" />
        </SpinnerOverlay>
      )}
      {!loading && (
        <AnimationRevealPage>
          <PresentationHeader
            heading={<>Registra tu visita <HighlightedText>fácil y seguro.</HighlightedText></>}
            description="Genera una invitación digital con QR anti-fraude en segundos. El código es único para cada visitante, cambia cada 30 segundos y solo puede usarse una vez."
            imageSrc={visitorImageSrc}
            imageCss={imageCss}
            imageDecoratorBlob={true}
            primaryButtonText="Comenzar"
            primaryButtonUrl="#visitasForm"
            watchVideoButtonText="Tutorial"
          />
          <VisitorConfirmation
            subheading={<Subheading>BizCode GDL</Subheading>}
            heading={
              <>
                Invitaciones QR
                <wbr /> <HighlightedText>seguras y únicas.</HighlightedText>
              </>
            }
            description={
              <Description>
                Llena el formulario con los datos de tu visitante o proveedor.
                Elige las fechas de su visita y el motivo. Al guardar, se genera automáticamente
                un enlace personal con QR dinámico que puedes compartir por WhatsApp.
              </Description>
            }
            buttonRounded={false}
            textOnLeft={false}
            primaryButtonText="Ver Visitas"
            primaryButtonUrl={ROUTE_PATHS.VISIT}
            imageSrc={historyVisitorImageSrc}
            imageCss={imageCss}
            imageDecoratorBlob={true}
            imageDecoratorBlobCss={tw`left-1/2 -translate-x-1/2 md:w-32 md:h-32 opacity-25`}
          />

          <div id="visitasForm"></div>
          <RegisterForm onVisitorCreated={handleVisitorCreated} />

          <Features
            heading={<>Recursos y <HighlightedText>Contacto.</HighlightedText></>}
            cards={[
              { imageSrc: adminIcon, title: "Administración", description: "Contacta al administrador del fraccionamiento para soporte, aclaraciones o cancelaciones.", url: "/contacto" },
              { imageSrc: policeIcon, title: "Caseta de Vigilancia", description: "Comunica a la caseta si tu visitante tiene alguna incidencia al llegar a la entrada.", url: "/contacto" },
              { imageSrc: celebrationIconImageSrc, title: "Proveedores Aprobados", description: "Consulta la lista oficial de proveedores y servicios autorizados en el fraccionamiento.", url: "/contacto" }
            ]}
          />

          <VisitorsHistory
            cards={visitorsHistory}
            subheading=""
            heading="Tu histórico de visitas realizadas."
          />
          <DownloadApp
            text={<>Próximamente espera por nuestra versión <HighlightedTextInverse>Móvil.</HighlightedTextInverse></>}
          />
          <Footer />
        </AnimationRevealPage>
      )}
      <NotificationContainer />
    </>
  );
};
